import { Link, Outlet, createRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { type NavGroup, visibleNavGroups } from "@/app/nav";
import { useSession } from "@/auth/session";
import { LG, MD, useMediaQuery } from "@/components/useMediaQuery";
import { rootRoute } from "@/routes/root";

export const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "staff",
  beforeLoad: ({ context, location }) => {
    if (!context.session.me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: StaffShell,
});

const OPEN_GROUPS_KEY = "cipm.nav.open";

// One open group, stored as a JSON string; older builds stored an array, which is ignored.
function readOpenGroup(): string | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? "null");
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}

function saveOpenGroup(label: string | null) {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(label));
  } catch {
    // Storage can be blocked; the menu still works for this visit.
  }
}

// The group holding the deepest nav item that matches the current path.
function activeGroup(groups: NavGroup[], pathname: string): string | null {
  let best: { label: string; length: number } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const hit = item.exact ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (hit && (!best || item.to.length > best.length)) best = { label: group.label, length: item.to.length };
    }
  }
  return best?.label ?? null;
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const groups = visibleNavGroups(can);
  const current = activeGroup(groups, pathname);
  const [openGroup, setOpenGroup] = useState<string | null>(() => current ?? readOpenGroup());

  useEffect(() => {
    if (!current) return;
    setOpenGroup(current);
    saveOpenGroup(current);
  }, [current]);

  const toggle = (label: string) => {
    const next = label === openGroup ? null : label;
    setOpenGroup(next);
    saveOpenGroup(next);
  };

  return (
    <div className="space-y-3 text-sm">
      {groups.map((group) => {
        const listId = `nav-group-${group.label.toLowerCase()}`;
        const expanded = openGroup === group.label;
        return (
          <div key={group.label}>
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={listId}
              onClick={() => toggle(group.label)}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-brand-ink hover:bg-brand-light/15"
            >
              {group.label}
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                className={`h-4 w-4 text-brand transition-transform ${expanded ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4l4 4-4 4" />
              </svg>
            </button>
            {/* Indented under a rule, so the pages read as belonging to their heading. */}
            <ul id={listId} aria-label={group.label} hidden={!expanded} className="ml-4 mt-1 space-y-1 border-l border-zinc-200 pl-2">
              {group.items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    aria-label={item.ariaLabel}
                    activeOptions={{ exact: item.exact ?? false }}
                    className="relative block w-full rounded-md px-3 py-2 text-left transition"
                    activeProps={{
                      className:
                        "bg-brand-light/20 font-medium text-brand-ink before:absolute before:inset-y-1.5 before:-left-2.5 before:w-0.5 before:rounded-full before:bg-brand-strong",
                    }}
                    inactiveProps={{ className: "text-zinc-700 hover:bg-zinc-100" }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function AccountMenu() {
  const { me, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  return (
    <div ref={root} className="relative text-sm">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={open ? "account-menu" : undefined}
        onClick={() => setOpen((o) => !o)}
        className="min-h-10 rounded-md px-3 text-white/90 hover:bg-white/10"
      >
        Account
      </button>
      {open && (
        <div id="account-menu" className="absolute right-0 top-full z-30 mt-1 w-56 space-y-1 rounded-md border border-zinc-200 bg-white p-2 shadow-lg">
          <p className="px-3 py-1 text-zinc-600">{me?.user.full_name}</p>
          <Link
            to="/account/security"
            onClick={() => setOpen(false)}
            className="block min-h-10 rounded-md px-3 py-2 text-zinc-700 hover:bg-zinc-100"
          >
            Account security
          </Link>
          <button
            type="button"
            onClick={() => void signOut()}
            className="block min-h-10 w-full rounded-md px-3 py-2 text-left text-zinc-700 hover:bg-zinc-100"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function StaffShell() {
  const { me, signOut } = useSession();
  const isMd = useMediaQuery(MD);
  const isLg = useMediaQuery(LG);
  const href = useRouterState({ select: (s) => s.location.href });
  const [open, setOpen] = useState(false);
  const drawerOpen = open && !isLg;
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  // A route change closes the drawer.
  useEffect(() => setOpen(false), [href]);
  // Reaching desktop clears the intent, so shrinking again doesn't reopen it.
  useEffect(() => {
    if (isLg) setOpen(false);
  }, [isLg]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key !== "Tab" || !drawer.current) return;
      // Links inside collapsed groups are hidden, so they are not tab stops.
      const items = [...drawer.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")].filter(
        (el) => !el.closest("[hidden]"),
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      // aria-modal alone doesn't stop Tab from reaching the page behind.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    drawer.current?.querySelector<HTMLElement>("ul:not([hidden]) a")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (wasOpen.current && !drawerOpen) menuButton.current?.focus();
    wasOpen.current = drawerOpen;
  }, [drawerOpen]);

  return (
    <div className="min-h-dvh bg-zinc-50 text-zinc-950">
      <header className="flex h-14 items-center justify-between bg-brand-ink px-4 text-white md:px-6">
        <div className="flex items-center gap-2">
          {!isLg && (
            <button
              ref={menuButton}
              type="button"
              aria-expanded={drawerOpen}
              aria-controls={drawerOpen ? "main-nav" : undefined}
              onClick={() => setOpen((o) => !o)}
              className="min-h-10 rounded-md px-3 text-sm text-white/90 hover:bg-white/10"
            >
              Menu
            </button>
          )}
          {/* The logo says CIPM; phones show it alone. */}
          <Link to="/" aria-label="Certificate Platform" className="flex items-center gap-2 font-semibold">
            <img src="/cipm-logo.png" alt="" className="h-7 w-7" />
            {isMd ? "Certificate Platform" : null}
          </Link>
        </div>
        {isMd ? (
          <div className="flex items-center gap-4 text-sm">
            <span className="border-r border-white/25 pr-4 text-white/65">{me?.user.full_name}</span>
            <Link to="/account/security" className="text-white/80 hover:text-white">
              Account security
            </Link>
            <button onClick={() => void signOut()} className="text-white/80 hover:text-white">
              Sign out
            </button>
          </div>
        ) : (
          <AccountMenu />
        )}
      </header>

      <div className="flex min-h-[calc(100dvh-3.5rem)]">
        {isLg && (
          <nav aria-label="Main" className="hidden w-56 border-r border-zinc-200 bg-white p-3 lg:block">
            <NavList />
          </nav>
        )}

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-40">
          <div data-testid="drawer-backdrop" className="absolute inset-0 bg-zinc-950/40" onClick={() => setOpen(false)} />
          <div
            ref={drawer}
            id="main-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            className="absolute inset-y-0 left-0 w-64 max-w-[85vw] overflow-y-auto bg-white p-3 shadow-xl"
          >
            <NavList onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
